import React, { useMemo } from "react";
import { StriimInputField } from "@striim/striim-ui";
import { Box } from "@mui/material";

import { PerformanceSettingsValues } from "../../panel/sentinel-panel-container";
import { DEFAULT_STRIIM_AI_ENGINE } from "../../../guardian/settings/settings-tab";

const batchTimeoutPeriodOptions = [
    { label: "SECOND", value: "s" },
    { label: "MINUTE", value: "m" }
];

const getTimeoutAndPeriod = (value: string) => {
    const period = value[value.length - 1];
    const timeout = value.slice(0, value.length - 1);
    const periodValue = batchTimeoutPeriodOptions.find(item => item.value === period);
    return [timeout, periodValue];
};

interface PerformanceSettingsProps {
    values: PerformanceSettingsValues;
    onChange: (property: string, value: any) => void;
    aiEngine: string;
    isDisabled: boolean;
}

const PerformanceSettings: React.FC<PerformanceSettingsProps> = ({ values, onChange, aiEngine, isDisabled }) => {
    const [batchTimeoutValue, batchTimeoutPeriodOption] = useMemo(() => getTimeoutAndPeriod(values.batchTimeout), [
        values.batchTimeout
    ]);

    return (
        <>
            <StriimInputField
                name="batchSize"
                label="Batch Size"
                type="number"
                disabled={isDisabled}
                value={values.batchSize}
                onChange={value => onChange("batchSize", value)}
            />
            <Box display={"flex"} alignItems={"flex-end"} gap={1}>
                <StriimInputField
                    name="batchTimeout"
                    label="Batch Timeout"
                    type="number"
                    sx={{ flex: 1 }}
                    disabled={isDisabled}
                    value={batchTimeoutValue}
                    onChange={value => {
                        onChange("batchTimeout", value + batchTimeoutPeriodOption.value);
                    }}
                />
                <StriimInputField
                    name="batchTimeoutPeriod"
                    label=""
                    select
                    SelectProps={{
                        dataTestId: "batchTimeoutPeriodSelect",
                        options: batchTimeoutPeriodOptions,
                        components: {
                            IndicatorSeparator: () => null,
                            ClearIndicator: () => null
                        }
                    }}
                    sx={{ flex: 1 }}
                    disabled={isDisabled}
                    value={batchTimeoutPeriodOption}
                    onChange={value => {
                        const batchTimeout = batchTimeoutValue + value.value;
                        onChange("batchTimeout", batchTimeout);
                    }}
                />
            </Box>

            {aiEngine === DEFAULT_STRIIM_AI_ENGINE ? (
                <StriimInputField
                    name="striimAIMode"
                    label="Striim AI Mode"
                    select
                    SelectProps={{
                        dataTestId: "striimAIModeSelect",
                        options: [
                            { label: "Basic", value: "BASIC" },
                            { label: "Advanced", value: "ADVANCED" }
                        ],
                        components: {
                            IndicatorSeparator: () => null,
                            ClearIndicator: () => null
                        }
                    }}
                    disabled={isDisabled}
                    value={values.striimAIMode}
                    onChange={value => onChange("striimAIMode", value)}
                />
            ) : (
                <StriimInputField
                    name="parallelRequestCount"
                    label="Parallel Request Count"
                    type="number"
                    disabled={isDisabled}
                    value={values.parallelRequestCount}
                    onChange={(value) => onChange("parallelRequestCount", value)}
                />
            )}
        </>
    );
};

export default PerformanceSettings;
